import { useTranslation } from 'react-i18next';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowDown,
  ArrowRight,
  Check,
  CircleX,
  Cloud,
  Laptop,
  LoaderCircle,
  Server,
} from 'lucide-react';
import type { ProjectCustomDomainInfo } from '@claude-code-api/shared';
import { Badge } from '@/components/ui';
import { cn } from '@/lib/utils';

type FlowState = 'passed' | 'connecting' | 'failed';

interface FlowNode {
  key: 'domain' | 'platform' | 'session';
  icon: LucideIcon;
  value: string;
  detail?: string;
  state: FlowState;
}

interface ProjectCustomDomainFlowProps {
  domain: ProjectCustomDomainInfo;
  checking: boolean;
  projectName: string;
}

const stateStyles: Record<FlowState, {
  accent: string;
  surface: string;
  badge: 'success' | 'warning' | 'destructive';
  icon: LucideIcon;
}> = {
  passed: {
    accent: 'text-success',
    surface: 'border-success/40 bg-success/10 text-success',
    badge: 'success',
    icon: Check,
  },
  connecting: {
    accent: 'text-warning',
    surface: 'border-warning/40 bg-warning/10 text-warning',
    badge: 'warning',
    icon: LoaderCircle,
  },
  failed: {
    accent: 'text-destructive',
    surface: 'border-destructive/40 bg-destructive/10 text-destructive',
    badge: 'destructive',
    icon: CircleX,
  },
};

function getDnsState(domain: ProjectCustomDomainInfo): FlowState {
  if (domain.status === 'active') return 'passed';
  if (domain.status === 'pending' || domain.status === 'deleting') return 'connecting';
  return 'failed';
}

function getRouteState(domain: ProjectCustomDomainInfo): FlowState {
  if (domain.reconciliationError || domain.status === 'suspended') return 'failed';
  if (domain.status === 'pending' || domain.status === 'deleting') return 'connecting';
  return domain.targetStatus === 'running' ? 'passed' : 'failed';
}

function FlowConnector({ state }: { state: FlowState }) {
  const { t } = useTranslation('projects');
  const styles = stateStyles[state];

  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center py-1 sm:w-20 sm:py-0',
        styles.accent,
      )}
      aria-label={t(`deployments.customDomain.flow.status.${state}`)}
    >
      <ArrowDown className="h-5 w-5 sm:hidden" />
      <ArrowRight className="hidden h-6 w-6 sm:block" />
      <span className="sr-only">{t(`deployments.customDomain.flow.status.${state}`)}</span>
    </div>
  );
}

function FlowEndpoint({ node }: { node: FlowNode }) {
  const { t } = useTranslation('projects');
  const styles = stateStyles[node.state];
  const EndpointIcon = node.icon;
  const StateIcon = styles.icon;

  return (
    <div className="min-w-0 flex-1">
      <div className="flex h-full flex-col items-center px-4 py-4 text-center">
        <div className="relative">
          <div className={cn('flex h-14 w-14 items-center justify-center rounded-full border-2', styles.surface)}>
            <EndpointIcon className="h-6 w-6" />
          </div>
          <span className={cn(
            'absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-background bg-background',
            styles.accent,
          )}>
            <StateIcon className={cn('h-4 w-4', node.state === 'connecting' && 'animate-spin')} />
          </span>
        </div>
        <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {t(`deployments.customDomain.flow.${node.key}`)}
        </p>
        <p className="mt-1 w-full max-w-full truncate font-mono text-sm font-medium" title={node.value}>
          {node.value}
        </p>
        {node.detail && (
          <p className="mt-1 max-w-full break-all font-mono text-xs text-muted-foreground">
            {node.detail}
          </p>
        )}
        <Badge variant={styles.badge} className="mt-3 gap-1">
          <StateIcon className={cn('h-3 w-3', node.state === 'connecting' && 'animate-spin')} />
          {t(`deployments.customDomain.flow.status.${node.state}`)}
        </Badge>
      </div>
    </div>
  );
}

export function ProjectCustomDomainFlow({ domain, checking, projectName }: ProjectCustomDomainFlowProps) {
  const { t } = useTranslation('projects');
  const dnsState = checking ? 'connecting' : getDnsState(domain);
  const routeState = getRouteState(domain);
  const publicAppLabel = domain.sessionId ? projectName : t('deployments.customDomain.unbound');
  const nodes: FlowNode[] = [
    {
      key: 'domain',
      icon: Laptop,
      value: domain.hostname,
      state: 'passed',
    },
    {
      key: 'platform',
      icon: Server,
      value: domain.setupRecord.value,
      state: dnsState,
    },
    {
      key: 'session',
      icon: Cloud,
      value: publicAppLabel,
      state: routeState,
    },
  ];

  return (
    <div
      className="flex flex-col sm:flex-row sm:items-stretch"
      aria-label={t('deployments.customDomain.flow.label')}
    >
      <FlowEndpoint node={nodes[0]} />
      <FlowConnector state={dnsState} />
      <FlowEndpoint node={nodes[1]} />
      <FlowConnector state={routeState} />
      <FlowEndpoint node={nodes[2]} />
    </div>
  );
}
